<script setup>
import { defineModel, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';
import DropdownContainer from 'next/dropdown-menu/base/DropdownContainer.vue';
import DropdownSection from 'next/dropdown-menu/base/DropdownSection.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import DropdownItem from 'next/dropdown-menu/base/DropdownItem.vue';

const { t } = useI18n();
const selected = defineModel({
  type: String,
  default: '',
});

const isDarkMode = computed(() => {
  return document.getElementsByTagName('BODY')[0].classList.contains('dark');
});

const getColorValue = colorKey => {
  const color = wootConstants.COLORS[colorKey];
  return isDarkMode.value ? color.dark : color.light;
};

const colorOptions = computed(() => {
  return Object.entries(wootConstants.COLORS).map(([key, colorData]) => ({
    key,
    name: colorData.description,
    value: getColorValue(key),
  }));
});

const selectedColor = computed(() => {
  if (!selected.value) return null;
  return (
    colorOptions.value.find(option => option.key === selected.value) || null
  );
});

const selectColor = colorKey => {
  selected.value = colorKey === selected.value ? '' : colorKey;
};
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle }">
      <button
        class="py-1.5 px-3 text-n-slate-12 bg-n-alpha-1 text-sm rounded-lg reset-base w-full border border-n-weak flex items-center justify-between gap-2 hover:bg-n-alpha-2"
        @click="toggle"
      >
        <div class="flex items-center gap-2">
          <div
            v-if="selectedColor"
            class="w-4 h-4 rounded border border-n-weak"
            :style="{ backgroundColor: selectedColor.value }"
          />
          <span>{{
            selectedColor ? selectedColor.name : t('FILTER.SELECT_COLOR')
          }}</span>
        </div>
        <span class="i-lucide-chevron-down size-3" />
      </button>
    </template>
    <DropdownBody class="top-0 min-w-80 z-50" strong>
      <DropdownSection>
        <div class="p-2">
          <div class="grid grid-cols-10 gap-1">
            <DropdownItem
              v-for="color in colorOptions"
              :key="color.key"
              class="!p-0"
              :preserve-open="false"
              @click="selectColor(color.key)"
            >
              <div
                v-tooltip="color.name"
                class="w-6 h-6 rounded border cursor-pointer transition-all hover:scale-110 hover:border-n-slate-6"
                :class="{
                  'border-2 border-n-slate-9 scale-110': selected === color.key,
                  'border-n-weak': selected !== color.key,
                }"
                :style="{ backgroundColor: color.value }"
              />
            </DropdownItem>
          </div>
          <div v-if="selected" class="mt-3 pt-2 border-t border-n-weak">
            <DropdownItem :preserve-open="false" @click="selectColor('')">
              <template #label>
                {{ t('FILTER.CLEAR_SELECTION') }}
              </template>
            </DropdownItem>
          </div>
        </div>
      </DropdownSection>
    </DropdownBody>
  </DropdownContainer>
</template>
